/**
 * Custom Logger Utility
 * 
 * This utility provides enhanced logging capabilities that can output to both
 * browser console and potentially forward to server terminal.
 */

/**
 * Custom logger that outputs to console and attempts terminal forwarding
 */
export const logger = {
  /**
   * Log info message
   * @param {string} message - Log message
   * @param {object} data - Additional data to log
   */
  info: (message, data = {}) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      level: 'INFO',
      message,
      data,
      timestamp
    };
    
    // Browser console
    console.log(`[${timestamp}] ${message}`, data);
    
    // Attempt to forward to terminal (if running in development)
    if (process.env.NODE_ENV === 'development') {
      try {
        // This will show in the terminal where npm start is running
        console.info(`🔵 [CLIENT-LOG] ${message}`, JSON.stringify(data, null, 2));
      } catch (error) {
        // Fallback to regular console
        console.log('Logger forwarding failed:', error);
      }
    }
  },

  /**
   * Log error message
   * @param {string} message - Error message
   * @param {object} data - Additional error data
   */
  error: (message, data = {}) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      level: 'ERROR',
      message,
      data,
      timestamp
    };
    
    // Browser console
    console.error(`[${timestamp}] ${message}`, data);
    
    // Attempt to forward to terminal (if running in development)
    if (process.env.NODE_ENV === 'development') {
      try {
        // This will show in the terminal where npm start is running
        console.error(`🔴 [CLIENT-ERROR] ${message}`, JSON.stringify(data, null, 2));
      } catch (error) {
        // Fallback to regular console
        console.error('Logger forwarding failed:', error);
      }
    }
  },

  /**
   * Log warning message
   * @param {string} message - Warning message
   * @param {object} data - Additional warning data
   */
  warn: (message, data = {}) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      level: 'WARN',
      message,
      data,
      timestamp
    };
    
    // Browser console
    console.warn(`[${timestamp}] ${message}`, data);
    
    // Attempt to forward to terminal (if running in development)
    if (process.env.NODE_ENV === 'development') {
      try {
        // This will show in the terminal where npm start is running
        console.warn(`🟡 [CLIENT-WARN] ${message}`, JSON.stringify(data, null, 2));
      } catch (error) {
        // Fallback to regular console
        console.warn('Logger forwarding failed:', error);
      }
    }
  },

  /**
   * Log debug message (only in development)
   * @param {string} message - Debug message
   * @param {object} data - Additional debug data
   */
  debug: (message, data = {}) => {
    if (process.env.NODE_ENV === 'development') {
      const timestamp = new Date().toISOString();
      console.debug(`[${timestamp}] ${message}`, data);
      
      try {
        console.debug(`🟣 [CLIENT-DEBUG] ${message}`, JSON.stringify(data, null, 2));
      } catch (error) {
        console.debug('Logger forwarding failed:', error);
      }
    }
  }
};

export default logger;
